import { defineStore } from 'pinia';
import {
  loadManifest,
  loadProgress,
  loadMissing,
  loadAnalyses,
  loadAnalyzeProgress,
  loadFailed,
  startScan,
  indexCount,
  aggregate,
} from '@/api/research';

// US states (codes for jurisdictions we may eventually cover) — used for
// quickly looking up a friendly name from a code present in the data.
const JURISDICTION_NAMES = {
  US: 'Federal (IRS)',
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
  CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', DC: 'District of Columbia',
  FL: 'Florida', GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois',
  IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana',
  ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts', MI: 'Michigan',
  MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri', MT: 'Montana',
  NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey',
  NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota',
  OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania',
  RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota', TN: 'Tennessee',
  TX: 'Texas', UT: 'Utah', VT: 'Vermont', VA: 'Virginia', WA: 'Washington',
  WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
};

// The frontend no longer talks to OpenSearch directly — everything goes
// through the Lambda. These fields are kept (with placeholder values) so
// existing components that still pass `store.opensearchUrl` / `store.indexName`
// into the API helpers compile; the helpers ignore them.
export const useResearchStore = defineStore('research', {
  state: () => ({
    opensearchUrl: '(proxied via /api)',
    indexName: localStorage.getItem('strr.indexName') || 'tax-documents-v2',
    manifestDocs: [],
    manifestLoadedAt: null,
    progress: null,
    progressUpdatedAt: null,
    missing: null,
    indexCount: 0,
    opensearchHealthy: false,
    loading: false,
    error: null,
    docKindBreakdown: {},
    yearBreakdown: {},
    jurisdictionStats: {},
    analyses: {},
    analysesGeneratedAt: null,
    analyzeProgress: null,
    failed: {},
    failedUpdatedAt: null,
    scanStarting: false,
    scanStartError: null,
    lastStartedScan: null,
  }),

  getters: {
    jurisdictionList() {
      return Object.entries(this.jurisdictionStats)
        .map(([code, stats]) => ({
          code,
          name: JURISDICTION_NAMES[code] || code,
          ...stats,
        }))
        .sort((a, b) => b.documents - a.documents);
    },

    formsByJurisdiction() {
      const byJur = {};
      for (const doc of this.manifestDocs) {
        if (!doc.formName) continue;
        const list = (byJur[doc.jurisdiction] = byJur[doc.jurisdiction] || {});
        const entry = (list[doc.formName] = list[doc.formName] || {
          formName: doc.formName,
          jurisdiction: doc.jurisdiction,
          years: new Set(),
          kinds: new Set(),
          latest: null,
          docs: [],
          hasEmbeddedInstructions: false,
        });
        if (doc.taxYear) entry.years.add(doc.taxYear);
        if (doc.docKind) entry.kinds.add(doc.docKind);
        // Treat a form PDF that has instructions baked in as also covering
        // the "instructions" requirement, even when there's no separate
        // instructions doc indexed.
        if (doc.docKind === 'form' && doc.embeddedInstructions) {
          entry.kinds.add('instructions');
          entry.hasEmbeddedInstructions = true;
        }
        entry.docs.push(doc);
        if (!entry.latest || (doc.indexedAt || '') > (entry.latest || '')) {
          entry.latest = doc.indexedAt;
        }
      }
      return byJur;
    },

    allForms() {
      const flat = [];
      const map = this.formsByJurisdiction;
      for (const [jur, forms] of Object.entries(map)) {
        for (const form of Object.values(forms)) {
          flat.push({
            ...form,
            jurisdiction: jur,
            jurisdictionName: JURISDICTION_NAMES[jur] || jur,
            years: Array.from(form.years).sort((a, b) => b - a),
            kinds: Array.from(form.kinds),
            hasEmbeddedInstructions: !!form.hasEmbeddedInstructions,
          });
        }
      }
      return flat.sort((a, b) =>
        a.jurisdiction === b.jurisdiction
          ? a.formName.localeCompare(b.formName)
          : a.jurisdiction.localeCompare(b.jurisdiction)
      );
    },

    recentlyIndexed() {
      return [...this.manifestDocs]
        .filter((doc) => doc.indexedAt)
        .sort((a, b) => (b.indexedAt || '').localeCompare(a.indexedAt || ''))
        .slice(0, 50);
    },

    jurisdictionName: () => (code) => JURISDICTION_NAMES[code] || code,

    analysesList() {
      return Object.values(this.analyses).sort((a, b) =>
        (b.analyzed_at || '').localeCompare(a.analyzed_at || '')
      );
    },

    analysisFor: (state) => (documentUrl) => state.analyses[documentUrl] || null,

    failedList() {
      return Object.values(this.failed).sort((a, b) =>
        (b.recorded_at || '').localeCompare(a.recorded_at || '')
      );
    },

    formVersionTimeline() {
      // For each (jurisdiction, formName), build a flat sorted timeline of
      // every indexed version across every URL that document was published at.
      const grouped = {};
      for (const doc of this.manifestDocs) {
        if (!doc.formName) continue;
        const key = `${doc.jurisdiction}|${doc.formName}|${doc.docKind}`;
        const bucket = (grouped[key] = grouped[key] || {
          key,
          jurisdiction: doc.jurisdiction,
          formName: doc.formName,
          docKind: doc.docKind,
          versions: [],
        });
        for (const v of doc.versions || []) {
          bucket.versions.push({
            sha256: v.sha256,
            indexedAt: v.indexed_at || v.indexedAt,
            chunkCount: v.chunk_count || v.chunkCount,
            contentType: v.content_type,
            taxYear: doc.taxYear,
            sourceUrl: doc.url,
          });
        }
      }
      const out = Object.values(grouped);
      for (const row of out) {
        row.versions.sort((a, b) => (a.indexedAt || '').localeCompare(b.indexedAt || ''));
        row.first = row.versions[0]?.indexedAt;
        row.last = row.versions[row.versions.length - 1]?.indexedAt;
      }
      out.sort((a, b) =>
        a.jurisdiction === b.jurisdiction
          ? a.formName.localeCompare(b.formName)
          : a.jurisdiction.localeCompare(b.jurisdiction)
      );
      return out;
    },
  },

  actions: {
    setConfig({ opensearchUrl, indexName }) {
      if (opensearchUrl !== undefined) {
        this.opensearchUrl = opensearchUrl;
        localStorage.setItem('strr.opensearchUrl', opensearchUrl);
      }
      if (indexName !== undefined) {
        this.indexName = indexName;
        localStorage.setItem('strr.indexName', indexName);
      }
    },

    async refreshAll() {
      this.loading = true;
      this.error = null;
      try {
        await Promise.all([
          this.refreshManifest(),
          this.refreshProgress(),
          this.refreshOpenSearch(),
          this.refreshMissing(),
          this.refreshAnalyses(),
          this.refreshAnalyzeProgress(),
          this.refreshFailed(),
        ]);
      } catch (error) {
        this.error = error.message;
      } finally {
        this.loading = false;
      }
    },

    async startScan(payload) {
      this.scanStarting = true;
      this.scanStartError = null;
      try {
        this.lastStartedScan = await startScan(payload);
        await this.refreshProgress();
        return this.lastStartedScan;
      } catch (error) {
        this.scanStartError = error.message;
        throw error;
      } finally {
        this.scanStarting = false;
      }
    },

    async refreshAnalyses() {
      try {
        const data = await loadAnalyses();
        if (data) {
          this.analyses = data.analyses || {};
          this.analysesGeneratedAt = data.generated_at || null;
        }
      } catch (_) {
        // no-op — analyses file may not exist yet
      }
    },

    async refreshAnalyzeProgress() {
      try {
        this.analyzeProgress = await loadAnalyzeProgress();
      } catch (_) {
        this.analyzeProgress = null;
      }
    },

    async refreshFailed() {
      try {
        const data = await loadFailed();
        if (data) {
          this.failed = data.failures || {};
          this.failedUpdatedAt = data.updated_at || null;
        }
      } catch (_) {
        // ignore — may not exist yet
      }
    },

    async refreshManifest() {
      try {
        const manifest = await loadManifest();
        const docs = Object.entries(manifest.documents || {}).map(([url, entry]) => {
          const candidate = entry.candidate || {};
          const versions = entry.versions && entry.versions.length
            ? entry.versions
            : entry.sha256
              ? [{ sha256: entry.sha256, indexed_at: entry.indexed_at, chunk_count: entry.chunk_count }]
              : [];
          return {
            url,
            title: candidate.title || url,
            docKind: candidate.doc_kind || 'unknown',
            jurisdiction: candidate.jurisdiction_code || 'UNK',
            jurisdictionName:
              candidate.jurisdiction || JURISDICTION_NAMES[candidate.jurisdiction_code] || 'Unknown',
            formName: candidate.form_name || null,
            taxYear: candidate.tax_year || null,
            category: candidate.category || null,
            indexedAt: entry.indexed_at || null,
            chunkCount: Number(entry.chunk_count || 0),
            sha256: entry.sha256 || null,
            supported: candidate.supported,
            versions,
            versionCount: versions.length,
            firstSeenAt: entry.first_seen_at || entry.indexed_at || null,
            lastSeenAt: entry.last_seen_at || entry.indexed_at || null,
            contentType: entry.content_type || null,
            embeddedInstructions: !!(entry.embedded_instructions && entry.embedded_instructions.detected),
            embeddedSignals: entry.embedded_instructions?.signals || [],
          };
        });
        this.manifestDocs = docs;
        this.manifestLoadedAt = new Date();

        const jurStats = {};
        const docKindCounts = {};
        const yearCounts = {};
        for (const doc of docs) {
          const stats = (jurStats[doc.jurisdiction] = jurStats[doc.jurisdiction] || {
            documents: 0,
            forms: 0,
            instructions: 0,
            years: new Set(),
            distinctForms: new Set(),
            lastIndexed: null,
          });
          stats.documents += 1;
          if (doc.docKind === 'form') stats.forms += 1;
          if (doc.docKind === 'instructions') stats.instructions += 1;
          if (doc.taxYear) stats.years.add(doc.taxYear);
          if (doc.formName) stats.distinctForms.add(doc.formName);
          if ((doc.indexedAt || '') > (stats.lastIndexed || '')) {
            stats.lastIndexed = doc.indexedAt;
          }
          docKindCounts[doc.docKind] = (docKindCounts[doc.docKind] || 0) + 1;
          if (doc.taxYear) {
            yearCounts[doc.taxYear] = (yearCounts[doc.taxYear] || 0) + 1;
          }
        }
        for (const stats of Object.values(jurStats)) {
          stats.years = Array.from(stats.years).sort((a, b) => b - a);
          stats.distinctForms = stats.distinctForms.size;
        }
        this.jurisdictionStats = jurStats;
        this.docKindBreakdown = docKindCounts;
        this.yearBreakdown = yearCounts;
      } catch (error) {
        this.error = `manifest: ${error.message}`;
      }
    },

    async refreshProgress() {
      try {
        this.progress = await loadProgress();
        this.progressUpdatedAt = new Date();
      } catch (error) {
        this.progress = null;
      }
    },

    async refreshMissing() {
      try {
        this.missing = await loadMissing();
      } catch (_) {
        this.missing = null;
      }
    },

    async refreshOpenSearch() {
      try {
        this.indexCount = await indexCount(this.opensearchUrl, this.indexName);
        this.opensearchHealthy = true;
      } catch (error) {
        this.indexCount = 0;
        this.opensearchHealthy = false;
      }
    },

    async fetchAggregations() {
      if (!this.opensearchHealthy) return null;
      return aggregate(this.opensearchUrl, this.indexName, {
        by_doc_kind: { terms: { field: 'doc_kind', size: 20 } },
        by_jurisdiction: { terms: { field: 'jurisdiction_code', size: 60 } },
        by_year: { terms: { field: 'tax_year', size: 10 } },
        by_form: { terms: { field: 'form_name', size: 50 } },
      });
    },
  },
});
